import { useMemo } from "react";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { SingleProduct } from "src/types/graphql.d";
import { formatName } from "src/utils/formatName";

interface ProductBySlugData {
  productBySlug?: SingleProduct | null;
}

interface ProductBySlugVars {
  slug: string;
}

export const GET_PRODUCT_QUERY = `query productBySlug($slug: String!) {
    productBySlug(slug: $slug) {
      id
      name
      status
      gender
      description
      material
      price
      main_category_ids
      categories
      regular_price
      lowest_price
      slug
      images
      size_schema
      size_guide {
        text
        gender
      }
      is_currency_converted
      fit_size
      size_fit_label
      care_instructions
      promotion {
        promo_display_type
        promo_message
        promo_code
        promo_message_url
      }
      gate {
        level
      }
      brand {
        id
        name
        slug
        good_on_you_info {
          score_meta
          certifications {
            id
            name
          }
          scores {
            animals
            people
            planet
            overall
          }
          pillars {
            animals
            planet
            people
            overall
          }
        }
        about_popup_image
        about_brand_text
      }
      variations {
        id
        size
        c_size {
          type
          size
        }
        stock_quantity
        mapped_size {
          id
          value
          schema
          unit
          stock_quantity
          mappings {
            id
            value
            schema
            unit
          }
          irregular
          fallback {
            id
            value
            schema
            unit
            mappings {
              id
              value
              schema
              unit
            }
          }
        }
      }
      shipping {
        shipping_text
      }
      payment_types {
        name
        image
      }
      labels {
        priority
        color
        text_color
        background_color
        value
        type
      }
      kids_sub_categories {
        id
        name
        slug
      }
      translated_category_names
      translated_category_slugs
      translated_category_ids
      product_discount
      product_shop_type
      product_kids_gender
      sku
      currency {
        code
      }
    }
  }`;

export const GET_PRODUCT = gql`
  ${GET_PRODUCT_QUERY}
`;

const useProductBySlug = (
  slug: string,
  { skip = false }: { skip?: boolean } = {}
) => {
  const { error, data } = useQuery<ProductBySlugData, ProductBySlugVars>(
    GET_PRODUCT,
    {
      variables: { slug },
      fetchPolicy: "cache-first",
      skip,
    }
  );

  const product: SingleProduct | undefined = useMemo(() => {
    const fetched = data?.productBySlug;

    if (!fetched) {
      return undefined;
    }

    return {
      ...fetched,
      name: formatName(fetched.name || ""),
      brand: {
        ...fetched.brand,
        name: formatName(fetched.brand?.name || ""),
      },
    };
  }, [data?.productBySlug]);

  const loading = !skip && !data && !error;

  return { loading, error, product };
};

export { useProductBySlug };
